import { keyframes } from "@emotion/react";
import { rawColorValues } from "./colors";
import { ButtonVariants, Colors, FontWeight, radii } from "./enums";
// button effects
export const buttonLoadingEffect = keyframes `
  0%{background-position:0% 51%}
  50%{background-position:100% 50%}
  100%{background-position:0% 51%}
`;
export const buttonStyles = {
    cursor: "pointer",
    variant: "text.label2",
    fontWeight: FontWeight.textBold,
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    textDecoration: "none",
    display: "flex",
    height: "2.875rem",
    px: 16,
    borderRadius: radii.clickable,
    border: "none",
    m: 0,
    transition: "color 0.1s ease-in, background-color 0.1s ease-in",
    borderWidth: "1px",
    borderStyle: "solid",
    position: "relative",
    "&.loading": {
        cursor: "wait",
        zIndex: 0,
        overflow: "hidden"
    },
    "&.loading:after": {
        content: '" "',
        position: "absolute",
        backgroundSize: "600%",
        top: -2,
        left: -2,
        zIndex: -1,
        width: "calc(100% + 4px)",
        height: "calc(100% + 4px)"
    }
};
export const pillBoxStyle = (selected) => ({
    flexGrow: 1,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    cursor: "pointer",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderColor: "grey.2",
    color: selected ? "grey.0" : "grey.1",
    position: "relative",
    ":last-child": {
        borderRightWidth: 0
    },
    ":last-child ::after": {
        borderTopRightRadius: radii.clickable,
        borderBottomRightRadius: radii.clickable
    },
    ":first-of-type ::after": {
        borderTopLeftRadius: radii.clickable,
        borderBottomLeftRadius: radii.clickable
    }
});
export const pillLabelStyle = (isSelected) => ({
    width: "auto",
    "::after": {
        content: '" "',
        width: isSelected ? "calc(100% - 2px) " : "100%",
        height: isSelected ? "calc(100% - 2px) " : "100%",
        borderWidth: isSelected ? "2px" : "1px",
        borderStyle: isSelected ? "solid" : "none",
        borderColor: isSelected ? "grey.0" : "grey.2",
        top: "-1px",
        left: "-1px",
        position: "absolute"
    }
});
export const ThemeButtons = {
    [ButtonVariants.primary]: Object.assign(Object.assign({}, buttonStyles), { color: "white.0", bg: "blue.2", zIndex: 0, borderColor: "blue.2", "&:hover": {
            color: "white.0",
            bg: "blue.0",
            borderColor: "blue.0"
        }, "&:active": {
            color: "white.0",
            borderColor: "blue.2",
            bg: "blue.2"
        }, "&:after": {
            background: `linear-gradient(66deg, ${rawColorValues[Colors.blue][0]}, ${rawColorValues[Colors.blue][2]}, ${rawColorValues[Colors.black][1]})`,
            animation: `${buttonLoadingEffect} 5s linear infinite`
        }, "&:hover:after": {} }),
    [ButtonVariants.secondary]: Object.assign(Object.assign({}, buttonStyles), { bg: "white.0", color: "grey.1", borderColor: "grey.1", "&:hover": {
            color: "white.0",
            bg: "grey.0",
            borderColor: "grey.0"
        }, "&:active": {
            bg: "white.0",
            color: "white.0",
            borderColor: "grey.1"
        }, "&:after": {
            background: `linear-gradient(66deg, ${rawColorValues[Colors.white][0]}, ${rawColorValues[Colors.grey][4]}, ${rawColorValues[Colors.grey][3]})`,
            animation: `${buttonLoadingEffect} 5s linear infinite`
        }, "&.loading:hover": {
            color: "grey.1"
        }, "&:hover:after": {} }),
    [ButtonVariants.tertiary]: Object.assign(Object.assign({}, buttonStyles), { color: "white.0", bg: "blue.0", borderColor: "blue.0", "&:hover": {
            bg: "blue.1",
            borderColor: "blue.0"
        }, "&:active": {
            color: "white.0",
            bg: "blue.0"
        }, "&:after": {
            background: `linear-gradient(66deg, ${rawColorValues[Colors.blue][0]}, ${rawColorValues[Colors.blue][2]}, ${rawColorValues[Colors.black][1]})`,
            animation: `${buttonLoadingEffect} 5s linear infinite`
        }, "&:hover:after": {} }),
    [ButtonVariants.destructive]: Object.assign(Object.assign({}, buttonStyles), { color: "white.0", bg: "red.1", "&:hover": {
            bg: "red.2"
        }, "&:active": {
            color: "white.0",
            bg: "red.1"
        }, "&:after": {
            background: `linear-gradient(66deg, ${rawColorValues[Colors.red][0]}, ${rawColorValues[Colors.red][2]}, ${rawColorValues[Colors.black][1]})`,
            animation: `${buttonLoadingEffect} 5s linear infinite`
        }, "&:hover:after": {} }),
    [ButtonVariants.hollowLight]: Object.assign(Object.assign({}, buttonStyles), { borderStyle: "none", color: "grey.1", bg: "transparent", "&:active": {
            color: "grey.1"
        }, "&:hover": {
            color: "grey.0",
            bg: "transparent"
        } }),
    [ButtonVariants.hollowDark]: Object.assign(Object.assign({}, buttonStyles), { borderStyle: "none", color: "grey.2", bg: "transparent", "&:active": {
            color: "grey.2"
        }, "&:hover": {
            color: "white.0"
        } }),
    [ButtonVariants.outlineLight]: Object.assign(Object.assign({}, buttonStyles), { color: "grey.0", bg: "transparent", borderWidth: "1px", borderColor: "grey.4", "&:hover": {
            bg: "grey.4"
        }, "&[aria-expanded='true']": {
            bg: "grey.4"
        }, "&:active": {
            borderColor: "grey.1",
            color: "grey.1"
        } }),
    [ButtonVariants.outlineDark]: Object.assign(Object.assign({}, buttonStyles), { color: "grey.2", bg: "transparent", borderWidth: "1px", borderStyle: "solid", borderColor: "grey.2", "&:hover": {
            color: "white.0",
            borderColor: "white.0"
        }, "&:active": {
            color: "grey.2",
            borderColor: "grey.2"
        } }),
    [ButtonVariants.actionableTagPrimary]: {
        cursor: "pointer",
        variant: "text.label2",
        height: "2rem",
        px: 16,
        borderRadius: radii.pills,
        border: "none",
        margin: 0,
        transition: "all 0.1s ease-in",
        bg: "blue.0",
        display: "flex",
        color: "white.0",
        alignItems: "center",
        "&:hover": {
            bg: "blue.1"
        }
    },
    [ButtonVariants.actionableTagSecondary]: {
        cursor: "pointer",
        variant: "text.label2",
        height: "2rem",
        px: 16,
        borderRadius: radii.pills,
        border: "none",
        margin: 0,
        transition: "all 0.1s ease-in",
        display: "flex",
        alignItems: "center",
        color: "grey.1",
        bg: "white.0",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "grey.4",
        "&:hover": {
            color: "white.0",
            bg: "grey.0"
        }
    },
    [ButtonVariants.quantity]: {
        //height: "2.875rem",
        px: 16,
        //borderRadius: radii.clickable,
        //border: "none",
        //m: 0,
        //transition: "all 0.1s ease-in",
        //color: "grey.1",
        bg: "transparent",
        color: "grey.1",
        cursor: "pointer",
        //borderWidth: "1px",
        //borderStyle: "solid",
        //borderColor: "grey.1",
        //variant: "text.label2",
        // ":hover": {
        //   color: "grey.0",
        // },
        ":focus": {
            outline: "none"
        }
    },
    [ButtonVariants.accessible]: {
        cursor: "pointer",
        background: "transparent",
        border: "none",
        m: 0,
        p: 0
    }
};
//# sourceMappingURL=buttons.js.map